import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { countries } from '@/lib/seo/countries'

export const Route = createFileRoute(
  '/online-poker/countries/$country/$city',
)({
  loader: ({ params }) => {
    const country = countries.find((c) => c.slug === params.country)
    if (!country) throw notFound()
    const city = country.cities.find((c) => c.slug === params.city)
    if (!city) throw notFound()
    return { country, city }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const { country, city } = loaderData
    return {
      meta: [
        {
          title: `Online Poker in ${city.name}, ${country.name} | Powka`,
        },
        {
          name: 'description',
          content: `Online poker players in ${city.name}, ${country.name} are part of a global community that loves the game. Discover the poker scene in ${city.name}.`,
        },
        {
          property: 'og:title',
          content: `Online Poker in ${city.name}, ${country.name} | Powka`,
        },
        { property: 'og:type', content: 'article' },
      ],
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { country, city } = Route.useLoaderData()

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span>/</span>
        <Link to="/online-poker" className="hover:text-foreground">
          Online Poker
        </Link>
        <span>/</span>
        <Link to="/online-poker/countries" className="hover:text-foreground">
          Countries
        </Link>
        <span>/</span>
        <Link
          to="/online-poker/countries/$country"
          params={{ country: country.slug }}
          className="hover:text-foreground"
        >
          {country.name}
        </Link>
        <span>/</span>
        <span className="text-foreground">{city.name}</span>
      </nav>

      <h1 className="text-3xl font-bold mb-8">
        Online Poker in {city.name}, {country.name}
      </h1>

      <p className="text-muted-foreground leading-relaxed mb-10">
        {city.name} is one of {country.name}'s most dynamic cities, home to a
        community of poker players who enjoy the game at every level — from
        friendly home games to serious competitive play. Like players everywhere
        in {country.name}, those in {city.name} are part of a global network of
        enthusiasts who share a love for strategy, competition, and the
        excitement of no-limit Texas Hold'em.
      </p>

      <div className="mb-12">
        <h2 className="text-lg font-semibold mb-4">
          Poker in {country.name}
        </h2>
        <ul className="space-y-3">
          {[
            `${city.name} is part of ${country.name}'s broader poker community, which spans recreational players, hobbyists, and seasoned professionals`,
            `Players in ${city.name} enjoy the same global online poker ecosystem as their counterparts around the world`,
            `The poker culture in ${country.name} is reflected in cities like ${city.name}, where the game is enjoyed socially and competitively`,
            `Many players from ${city.name} participate in online poker platforms that connect them with opponents worldwide`,
            `Powka's USDC-based platform lets players in ${city.name} and beyond enjoy transparent, low-rake poker from anywhere`,
          ].map((fact, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
              <span className="text-muted-foreground">{fact}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border bg-secondary/30 px-6 py-5">
        <h2 className="font-semibold mb-1">Play on Powka</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Powka is a no-limit Texas Hold'em platform with a 1% rake. Deposits
          and withdrawals are handled in USDC — play from anywhere in the world,
          including {city.name}.
        </p>
        <Link
          to="/"
          className="inline-block rounded-md bg-foreground text-background text-sm font-medium px-4 py-2 hover:opacity-90 transition-opacity"
        >
          Start playing
        </Link>
      </div>

      <div className="mt-10 flex flex-col gap-2">
        <Link
          to="/online-poker/countries/$country"
          params={{ country: country.slug }}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to {country.name}
        </Link>
        <Link
          to="/online-poker/countries"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← View all countries
        </Link>
      </div>
    </div>
  )
}
